"use client";

import { useCallback, useEffect, useState, type ReactNode } from "react";
import { isRunLogEnabled, setRunLogEnabled } from "@/lib/learning-journey/debug/run-report-logger";
import type { JourneySnapshot, LearningJourney } from "@/lib/learning-journey/journey";
import type { Phase } from "@/lib/learning-journey/phase-fsm";
import type { PhraseState } from "@/lib/learning-journey/types";

/**
 * Dev-only overlay that surfaces the raw FSM state of the `LearningJourney`
 * and exposes manual triggers for every transition. Lives in
 * `components/learn-piece/` because it's specific to this route and is
 * scheduled to be deleted once the real state views land in chantier
 * 8b/8c/8d.
 *
 * Two halves, side by side:
 *
 *  - "Snapshot" lists every flat field of the snapshot (no nested objects)
 *    so we can eyeball the FSM at a glance during integration work.
 *  - "Force" is a flat list of buttons, one per `LearningJourney`
 *    command. We don't bother gating them on the current state, the
 *    FSM ignores unsupported events, and seeing nothing happen is
 *    informative enough during debug.
 */

const FORCE_PIECE_RATE = 0.9;

const VISIBILITY_STORAGE_KEY = "virtuosa:journey:debug-overlay-visible";

type DebugOverlayProps = {
  readonly snapshot: JourneySnapshot;
  readonly journey: LearningJourney;
  readonly songId: string;
};

function readInitialVisibility(): boolean {
  if (typeof window === "undefined") {
    return true;
  }

  const stored = window.localStorage.getItem(VISIBILITY_STORAGE_KEY);
  if (stored === null) {
    return true;
  }

  return stored === "1";
}

export function DebugOverlay({ snapshot, journey, songId }: DebugOverlayProps) {
  const phrase = snapshot.currentPhraseSnapshot;
  const phraseState: PhraseState | null = phrase ? phrase.state : null;
  const totalPhrases = snapshot.phraseProgress.length;
  const [visible, setVisible] = useState<boolean>(readInitialVisibility);
  const [lastAction, setLastAction] = useState<string | null>(null);

  const handleHide = useCallback(() => {
    setVisible(false);
    if (typeof window !== "undefined") {
      window.localStorage.setItem(VISIBILITY_STORAGE_KEY, "0");
    }
  }, []);

  const handleShow = useCallback(() => {
    setVisible(true);
    if (typeof window !== "undefined") {
      window.localStorage.setItem(VISIBILITY_STORAGE_KEY, "1");
    }
  }, []);

  const dispatch = useCallback((label: string, fn: () => void) => {
    fn();
    setLastAction(`${new Date().toLocaleTimeString()} · ${label}`);
  }, []);

  if (!visible) {
    return (
      <button
        type="button"
        onClick={handleShow}
        className="pointer-events-auto fixed bottom-5 right-16 z-40 rounded-md border border-border-subtle bg-bg-deep/90 px-3 py-1.5 font-mono text-[11px] text-text-secondary shadow-md backdrop-blur transition-colors hover:text-text-primary"
        title="Réafficher le panneau de debug"
      >
        Debug
      </button>
    );
  }

  return (
    <div className="pointer-events-auto fixed bottom-4 right-4 z-40 w-[460px] max-h-[80vh] overflow-y-auto rounded-lg border border-border-subtle bg-bg-deep/95 p-4 font-mono text-[11px] text-text-primary shadow-xl backdrop-blur">
      <div className="mb-3 flex items-baseline justify-between gap-2">
        <h2 className="text-[12px] font-semibold uppercase tracking-wide text-text-secondary">Journey debug</h2>
        <div className="flex items-center gap-2">
          <span className="truncate text-[10px] text-text-muted" title={songId}>
            {songId}
          </span>
          <button
            type="button"
            onClick={handleHide}
            className="rounded px-1.5 py-0.5 text-[11px] text-text-muted transition-colors hover:bg-white/[0.07] hover:text-text-primary"
            title="Masquer le panneau (réaffichable depuis le bouton 'Debug')"
          >
            ✕
          </button>
        </div>
      </div>

      <Section title="Snapshot">
        <Field label="phase" value={snapshot.phase} />
        <Field label="currentPhraseIndex" value={`${snapshot.currentPhraseIndex} / ${totalPhrases}`} />
        <Field label="phrase.state" value={phrase ? phrase.state : ", "} />
        <Field label="phrase.positionIndex" value={phrase ? String(phrase.positionIndex) : ", "} />
        <Field
          label="phrase.regime"
          value={
            phrase === null
              ? "n/a"
              : phrase.canLeaveTime
                ? "en rythme"
                : phrase.canEnterTime
                  ? "libre"
                  : "n/a"
          }
        />
        <Field
          label="phrase.pace"
          value={phrase?.pace == null ? ", " : phrase.pace.toFixed(2)}
        />
        <Field label="phrase.runHistory" value={phrase ? String(phrase.runHistory.length) : ", "} />
        <Field
          label="tempoSpeedupCourant"
          value={snapshot.tempoSpeedupCourant === null ? "null" : String(snapshot.tempoSpeedupCourant)}
        />
      </Section>

      <Section title="Calibration">
        <RunLogToggle />
      </Section>

      <Section title="Force next state">
        <ButtonGrid>
          <Button
            disabled={!isApplicable("acknowledgePresentChord", snapshot.phase, phraseState)}
            onClick={() => dispatch("acknowledgePresentChord", () => journey.acknowledgePresentChord())}
          >
            Acknowledge present chord
          </Button>
          <Button
            disabled={!isApplicable("confirmPosition", snapshot.phase, phraseState)}
            onClick={() => dispatch("confirmPosition", () => journey.confirmPosition())}
          >
            Confirm position
          </Button>
          <Button
            disabled={!isApplicable("completePracticeRun", snapshot.phase, phraseState)}
            onClick={() => dispatch("completePracticeRun(validated)", () => journey.completePracticeRun("validated"))}
          >
            Practice run validated
          </Button>
          <Button
            disabled={!isApplicable("completePracticeRun", snapshot.phase, phraseState)}
            onClick={() => dispatch("completePracticeRun(failed)", () => journey.completePracticeRun("failed"))}
          >
            Practice run failed
          </Button>
          <Button
            disabled={phrase === null || !phrase.canEnterTime}
            onClick={() => dispatch("enterTime", () => journey.enterTime())}
          >
            Enter time (S4)
          </Button>
          <Button
            disabled={phrase === null || !phrase.canLeaveTime}
            onClick={() => dispatch("leaveTime", () => journey.leaveTime())}
          >
            Leave time (free)
          </Button>
          <Button
            disabled={!isApplicable("startRhythmRun", snapshot.phase, phraseState)}
            onClick={() => dispatch("startRhythmRun", () => journey.startRhythmRun())}
          >
            Start rhythm run
          </Button>
          <Button
            disabled={!isApplicable("completeRhythmRun", snapshot.phase, phraseState)}
            onClick={() => dispatch("completeRhythmRun(validated)", () => journey.completeRhythmRun("validated"))}
          >
            Rhythm run validated
          </Button>
          <Button
            disabled={!isApplicable("completeRhythmRun", snapshot.phase, phraseState)}
            onClick={() => dispatch("completeRhythmRun(failed)", () => journey.completeRhythmRun("failed"))}
          >
            Rhythm run failed
          </Button>
          <Button
            disabled={phrase === null || !phrase.canStepBack}
            onClick={() => dispatch("stepBack", () => journey.stepBack())}
          >
            Step back
          </Button>
          <Button
            disabled={!isApplicable("setPieceRate", snapshot.phase, phraseState)}
            onClick={() => dispatch("setPieceRate", () => journey.setPieceRate(FORCE_PIECE_RATE))}
          >
            {`Set piece rate ${FORCE_PIECE_RATE}`}
          </Button>
          <Button
            disabled={!isApplicable("enterRepertoire", snapshot.phase, phraseState)}
            onClick={() => dispatch("enterRepertoire", () => journey.enterRepertoire())}
          >
            Played through (phase2 → repertoire)
          </Button>
          <Button danger onClick={() => dispatch("reset", () => journey.reset())}>
            Reset journey
          </Button>
        </ButtonGrid>

        {lastAction && <div className="mt-2 truncate text-[10px] text-text-muted">Last: {lastAction}</div>}
      </Section>
    </div>
  );
}

// ── Applicability ───────────────────────────────────────────────────

/**
 * Quick per-command applicability check, mirroring the FSM's own ignore
 * semantics. Disabling no-op buttons in the UI gives immediate feedback
 * that "nothing happens" is by design (wrong state) rather than a bug.
 */
function isApplicable(
  command:
    | "acknowledgePresentChord"
    | "confirmPosition"
    | "completePracticeRun"
    | "startRhythmRun"
    | "completeRhythmRun"
    | "setPieceRate"
    | "enterRepertoire",
  phase: Phase,
  state: PhraseState | null,
): boolean {
  switch (command) {
    case "setPieceRate":
      return phase === "phase2";
    case "enterRepertoire":
      return phase === "phase2";
    case "acknowledgePresentChord":
      return phase === "phase1" && state === "S0";
    case "confirmPosition":
      return phase === "phase1" && (state === "S1a" || state === "S2a");
    case "completePracticeRun":
      return phase === "phase1" && (state === "S1b" || state === "S2b" || state === "S3");
    case "startRhythmRun":
    case "completeRhythmRun":
      return phase === "phase1" && (state === "S4" || state === "S5");
  }
}

// ── Run-log toggle ──────────────────────────────────────────────────

/**
 * Toggle for the per-run JSON calibration log emitted by `useRhythmRun`.
 *
 * The log is opt-in (off by default) because emitting one JSON line per
 * S4/S5 run would clutter devtools during normal play. Activating it
 * persists the choice in localStorage (`virtuosa:journey:run-log`), so
 * a refresh keeps logging on and a calibration session can run
 * end-to-end without re-clicking after each navigation.
 *
 * Output goes to `console.info` and is grep-able by the
 * `[journey-run-log]` prefix. See
 * `lib/learning-journey/debug/run-report-logger.ts` for the JSON shape.
 */
function RunLogToggle() {
  // Lazy initial value: read once on mount. The flag is read-from /
  // written-to localStorage; we keep a local mirror so the button
  // re-renders without a global subscription.
  const [enabled, setEnabledLocal] = useState<boolean>(() => isRunLogEnabled());

  // Re-sync if another tab toggles the flag, devs working on
  // calibration occasionally do this from the console.
  useEffect(() => {
    const onStorage = () => {
      setEnabledLocal(isRunLogEnabled());
    };

    window.addEventListener("storage", onStorage);

    return () => {
      window.removeEventListener("storage", onStorage);
    };
  }, []);

  const handleToggle = useCallback(() => {
    const next = !enabled;
    setRunLogEnabled(next);
    setEnabledLocal(next);
  }, [enabled]);

  const label = enabled ? "Run log: ON (writing to console.info)" : "Run log: OFF (click to enable for calibration)";

  return (
    <button
      type="button"
      onClick={handleToggle}
      className={`w-full rounded border px-2 py-1.5 text-left text-[11px] transition-colors ${
        enabled
          ? "border-[#40d9c9]/50 bg-[#40d9c9]/15 text-text-primary"
          : "border-border-subtle bg-white/[0.03] text-text-secondary hover:bg-white/[0.07]"
      }`}
    >
      {label}
    </button>
  );
}

// ── Layout primitives ───────────────────────────────────────────────

function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div className="mb-3 last:mb-0">
      <div className="mb-1 text-[10px] font-semibold uppercase tracking-wide text-text-muted">{title}</div>
      {children}
    </div>
  );
}

function Field({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-baseline justify-between gap-3 py-0.5">
      <span className="text-text-muted">{label}</span>
      <span className="truncate font-medium">{value}</span>
    </div>
  );
}

function ButtonGrid({ children }: { children: ReactNode }) {
  return <div className="grid grid-cols-2 gap-1.5">{children}</div>;
}

function Button({
  children,
  onClick,
  danger = false,
  disabled = false,
}: {
  children: ReactNode;
  onClick: () => void;
  danger?: boolean;
  disabled?: boolean;
}) {
  const base =
    "rounded border px-2 py-1.5 text-left text-[11px] transition-colors disabled:opacity-40 disabled:cursor-not-allowed";
  const tone = danger
    ? "border-err/40 bg-err/10 text-err hover:bg-err/20"
    : "border-border-subtle bg-white/[0.03] text-text-primary hover:bg-white/[0.07]";

  return (
    <button type="button" onClick={onClick} disabled={disabled} className={`${base} ${tone}`}>
      {children}
    </button>
  );
}
